import * as Sentry from "@sentry/react";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  Circle,
  CircleCheck,
  CircleMinus,
  FileSearch
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { toast } from "sonner";

import { BrandLoader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ListCard, ListRow } from "@/components/ui/list-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";
import { Tag } from "@/components/ui/tag";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { CaseStrategyAPI } from "../../../api/caseStrategyApi";
import { legacyGenerateDraft } from "../../../api/drafts-v2";
import { QuestionnairesAPI } from "../../../api/questionnairesApi";
import type {
  CaseStrategy,
  ResearchPreviewRow,
  StrategyQuestion
} from "../../../api/types/classicDrafting";
import type { Draft } from "../../../api/types/draft";
import type { OptionType, Question } from "../../../hooks/useVisaQuestions";
import { ClassifyFilesDialog } from "../../caseFiles/components/ClassifyFilesDialog";
import { nodeTitle } from "../../caseFiles/nodeNames";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { autofillResult, topicFiles, type ClassicPick } from "./classicModel";
import { FillFromFilesDialog, pickableFiles } from "./FillFromFilesDialog";
import { ResearchTopicRows } from "./ResearchTopicRows";

const NO_IDS: string[] = [];
const POLL_MS = 3000;
const SAVE_DEBOUNCE_MS = 600;

type Props = {
  draft: Draft;
  pick: ClassicPick;
  research: ResearchPreviewRow[];
  /** Answers such as the field of expertise unblock research topics. */
  onAnswersSaved: () => void;
  onBack: () => void;
  onStarted: () => void;
};

/** Mirrors the backend's `has_content`: "N/A" reads as blank. */
export const hasContent = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === "string") {
    const text = value.trim();
    return !!text && text.toUpperCase() !== "N/A";
  }
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

/**
 * The merge-write for one answer. Flat text answers also refresh the
 * top-level `<field>.html` copy the old brief writers read first.
 */
export const answerWrite = (
  path: string[],
  value: unknown
): Record<string, unknown> => {
  let out: unknown = value;
  for (const part of [...path].reverse()) out = { [part]: out };
  const write = out as Record<string, unknown>;
  if (path.length === 1 && typeof value === "string") {
    write[`${path[0]}.html`] = value;
  }
  return write;
};

const optionValue = (o: string | OptionType): string =>
  typeof o === "string" ? o : o.value ?? o.optionText;
const optionLabel = (o: string | OptionType): string =>
  typeof o === "string" ? o : o.optionText;

const asText = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";

const SectionLabel: FC<{ title: string; meta?: string }> = ({
  title,
  meta
}) => (
  <div className="flex items-baseline gap-2">
    <span className="text-base font-medium text-fg">{title}</span>
    {meta ? <span className="text-sm text-fg-3">{meta}</span> : null}
  </div>
);

const AutofillStrip: FC<{
  data: CaseStrategy;
  onRetry: () => void;
  onReview: () => void;
}> = ({ data, onRetry, onReview }) => {
  const { status, filled, suggested, node_ids: picked, error } = data.autofill;
  const uncategorized = data.uncategorized
    ? ` ${data.uncategorized} ${
        data.uncategorized === 1 ? "file has" : "files have"
      } no category yet.`
    : "";
  if (status === "running") {
    return (
      <div className="flex items-center gap-2 border-b border-line-1 bg-info-soft px-3 py-1.5 text-sm text-fg-2">
        <Spinner className="size-3.5 shrink-0 text-info" />
        <span className="min-w-0 flex-1 truncate">
          <span className="text-fg">Filling From Case Files</span>
          {picked.length
            ? ` · Reading ${picked.length} ${
                picked.length === 1 ? "file" : "files"
              } for every question`
            : " · Categorizing files, then reading them for blank answers"}
        </span>
      </div>
    );
  }
  if (status === "no_text") {
    return (
      <div className="flex items-center gap-2 border-b border-line-1 bg-warn-soft px-3 py-1.5 text-sm text-fg-2">
        <AlertTriangle className="size-3.5 shrink-0 text-warn" />
        <span className="min-w-0 flex-1">
          <span className="text-fg">
            {error ?? "No readable case files yet"}.
          </span>
          {" Answer here, or fill from files once they're read."}
        </span>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex items-center gap-2 border-b border-line-1 bg-fill-1 px-3 py-1.5 text-sm text-fg-2">
        <AlertTriangle className="size-3.5 shrink-0 text-warn" />
        <span className="min-w-0 flex-1">
          Filling from case files didn't finish.
        </span>
        <Button variant="link" size="xs" onClick={onRetry}>
          Try Again
        </Button>
      </div>
    );
  }
  if (status === "done") {
    return (
      <div className="flex items-center gap-2 border-b border-line-1 bg-fill-1 px-3 py-1.5 text-sm text-fg-2">
        <CircleCheck className="size-3.5 shrink-0 text-success" />
        <span className="min-w-0 flex-1">
          <span className="text-fg">{autofillResult(filled, suggested)}</span>
          {uncategorized}
        </span>
        {data.uncategorized ? (
          <Button variant="link" size="xs" onClick={onReview}>
            Review
          </Button>
        ) : null}
      </div>
    );
  }
  return null;
};

const SourcesPopover: FC<{
  nodeIds: string[];
  nodesById: Map<string, string>;
}> = ({ nodeIds, nodesById }) => {
  const { open } = useCaseIde();
  return (
    <PopoverContent align="end" className="w-65 p-1">
      <div className="px-2 py-1.5 text-sm text-fg-3">Filled From</div>
      {nodeIds.map((id) => {
        const title = nodesById.get(id);
        return (
          <button
            key={id}
            type="button"
            disabled={!title}
            onClick={() => title && open({ kind: "file", id, title })}
            className="flex h-7 w-full items-center rounded-md px-2 text-left text-base text-fg hover:bg-fill-1 disabled:text-fg-4"
          >
            <span className="truncate">
              {title ?? "File no longer in the case"}
            </span>
          </button>
        );
      })}
    </PopoverContent>
  );
};

const FieldControl: FC<{
  question: Question;
  path: string[];
  value: unknown;
  onChange: (q: Question, path: string[], value: unknown) => void;
}> = ({ question: q, path, value, onChange }) => {
  const set = (next: unknown) => onChange(q, path, next);
  const options = q.options ?? [];
  switch (q.fieldType) {
    case "textarea":
      return (
        <Textarea
          value={asText(value)}
          onChange={(e) => set(e.target.value)}
          placeholder="Answer, or fill from case files"
          rows={3}
          aria-label={q.questionText}
        />
      );
    case "select":
      return (
        <Select value={asText(value) || undefined} onValueChange={set}>
          <SelectTrigger className="w-full" aria-label={q.questionText}>
            <SelectValue placeholder="Choose" />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={optionValue(o)} value={optionValue(o)}>
                {optionLabel(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "radio":
      return (
        <RadioGroup
          value={asText(value)}
          onValueChange={set}
          className="flex flex-wrap gap-4"
          aria-label={q.questionText}
        >
          {options.map((o) => {
            const id = `strategy-${q.variableName}-${optionValue(o)}`;
            return (
              <label
                key={optionValue(o)}
                htmlFor={id}
                className="flex items-center gap-2 text-base text-fg"
              >
                <RadioGroupItem id={id} value={optionValue(o)} />
                {optionLabel(o)}
              </label>
            );
          })}
        </RadioGroup>
      );
    case "checkbox": {
      const picked = Array.isArray(value) ? value.map(String) : [];
      return (
        <div className="flex flex-col gap-2">
          {options.map((o) => {
            const v = optionValue(o);
            const id = `strategy-${q.variableName}-${v}`;
            return (
              <label
                key={v}
                htmlFor={id}
                className="flex items-center gap-2 text-base text-fg"
              >
                <Checkbox
                  id={id}
                  checked={picked.includes(v)}
                  onCheckedChange={(on) =>
                    set(on ? [...picked, v] : picked.filter((x) => x !== v))
                  }
                />
                {optionLabel(o)}
              </label>
            );
          })}
        </div>
      );
    }
    case "period":
      return (
        <span className="text-sm text-fg-3">
          {hasContent(value) ? "Dates saved." : "No dates yet."} Edit dates in
          the case questionnaire.
        </span>
      );
    default:
      return (
        <Input
          value={asText(value)}
          onChange={(e) => set(e.target.value)}
          placeholder={
            q.fieldType === "wage"
              ? "$0 per year"
              : "Answer, or fill from case files"
          }
          aria-label={q.questionText}
        />
      );
  }
};

type FieldProps = {
  question: StrategyQuestion | Question;
  path: string[];
  answers: Record<string, unknown>;
  filling: boolean;
  fromCase: boolean;
  edited: boolean;
  nodesById: Map<string, string>;
  onChange: (q: Question, path: string[], value: unknown) => void;
  onPick?: () => void;
  onRetry?: (nodeIds: string[]) => void;
  onResolve?: (action: "accept" | "discard") => void;
};

const isStrategyQuestion = (
  q: StrategyQuestion | Question
): q is StrategyQuestion => "fill_from_files" in q;

const StrategyField: FC<FieldProps> = ({
  question: q,
  path,
  answers,
  filling,
  fromCase,
  edited,
  nodesById,
  onChange,
  onPick,
  onRetry,
  onResolve
}) => {
  const value = answers[q.variableName];
  const fill = isStrategyQuestion(q) ? q.fill_from_files : null;
  const run = fill?.run ?? null;
  const reading = run?.status === "running";
  const suggestion =
    run?.status === "done" && hasContent(run.suggestion)
      ? run.suggestion
      : null;
  const sources =
    fill && !edited && run?.status === "done" && !suggestion
      ? fill.sources
      : [];

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-5 items-center gap-1.5">
        <span className="min-w-0 flex-1 text-sm font-medium text-fg-2">
          {q.questionText}
          {q.isRequired ? (
            <span className="font-normal text-fg-4"> · Required</span>
          ) : null}
        </span>
        {reading ? (
          <span className="flex items-center gap-1.5 text-sm text-fg-3">
            <Spinner className="size-3" />
            Reading {run.node_ids.length}{" "}
            {run.node_ids.length === 1 ? "File" : "Files"}…
          </span>
        ) : sources.length ? (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="outline-none">
                <Tag>
                  From {sources.length}{" "}
                  {sources.length === 1 ? "File" : "Files"}
                  <ChevronDown />
                </Tag>
              </button>
            </PopoverTrigger>
            <SourcesPopover nodeIds={sources} nodesById={nodesById} />
          </Popover>
        ) : fromCase ? (
          <Tag>From Case Files</Tag>
        ) : null}
        {fill?.available && onPick ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-2xs"
                aria-label="Fill From Files"
                disabled={reading}
                onClick={onPick}
              >
                <FileSearch />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Fill From Files</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      {q.description ? (
        <span className="text-sm text-fg-3">{q.description}</span>
      ) : null}
      {filling ? (
        <div className="flex flex-col gap-1.5 rounded-md bg-fill-1 p-2">
          <Skeleton className="h-3 w-3/4" />
          {q.fieldType === "textarea" ? (
            <Skeleton className="h-3 w-1/2" />
          ) : null}
        </div>
      ) : (
        <FieldControl
          question={q}
          path={path}
          value={value}
          onChange={onChange}
        />
      )}
      {suggestion != null ? (
        <div className="flex flex-col gap-2 rounded-lg bg-fill-1 p-3">
          <span className="text-sm text-fg-3">
            Suggested from {run?.node_ids.length ?? 0}{" "}
            {run?.node_ids.length === 1 ? "file" : "files"}. Your answer stays
            until you use this.
          </span>
          <span className="text-base whitespace-pre-wrap text-fg">
            {asText(suggestion)}
          </span>
          <span className="flex gap-1.5">
            <Button size="xs" onClick={() => onResolve?.("accept")}>
              Use This
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => onResolve?.("discard")}
            >
              Discard
            </Button>
          </span>
        </div>
      ) : null}
      {run?.status === "empty" ? (
        <span className="text-sm text-fg-3">
          Nothing about this in the selected files.
        </span>
      ) : null}
      {run?.status === "failed" ? (
        <span className="flex items-center gap-1 text-sm text-danger">
          {run.error ?? "Couldn't read those files."}
          <Button
            variant="link"
            size="xs"
            onClick={() => onRetry?.(run.node_ids)}
          >
            Retry
          </Button>
        </span>
      ) : null}
      {q.subQuestions?.map((sub) => (
        <div key={sub.id} className="ml-3 border-l border-line-2 pl-3">
          <StrategyField
            question={sub}
            path={[sub.variableName]}
            answers={answers}
            filling={false}
            fromCase={false}
            edited={edited}
            nodesById={nodesById}
            onChange={onChange}
          />
        </div>
      ))}
    </div>
  );
};

type PreviewState = ResearchPreviewRow["state"];

const previewIcon = (states: PreviewState[]): ReactNode =>
  states.includes("blocked") ? (
    <AlertTriangle className="size-3.5 text-warn" />
  ) : states.includes("will_run") ? (
    <Circle className="size-3.5 text-fg-3" />
  ) : states.includes("ready") ? (
    <CircleCheck className="size-3.5 text-fg-3" />
  ) : (
    <CircleMinus className="size-3.5 text-fg-4" />
  );

const previewLabel = (states: PreviewState[]): ReactNode =>
  states.includes("blocked") ? (
    <StatusBadge tone="warn">Needs Answers</StatusBadge>
  ) : (
    <span className="text-sm text-fg-3">
      {states.includes("will_run")
        ? "Runs on Generate"
        : states.includes("ready")
        ? "Reused"
        : states.includes("omitted")
        ? "Omitted"
        : "Skipped"}
    </span>
  );

/** A topic the attorney can switch off: one that would run on Generate. */
const switchable = (r: ResearchPreviewRow): boolean =>
  r.state === "will_run" || r.state === "omitted";

/** The preview as it will run: switched-off topics read as `omitted`. */
export const withOmitted = (
  rows: ResearchPreviewRow[],
  omitted: ReadonlySet<string>
): ResearchPreviewRow[] =>
  rows.map((r) =>
    r.state === "will_run" && omitted.has(r.area)
      ? { ...r, state: "omitted" }
      : r
  );

const RunSwitch: FC<{
  title: string;
  on: boolean;
  onChange: (on: boolean) => void;
}> = ({ title, on, onChange }) => (
  <Switch
    size="sm"
    checked={on}
    aria-label={`Run ${title}`}
    onCheckedChange={onChange}
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
  />
);

export const ResearchPreview: FC<{
  rows: ResearchPreviewRow[];
  /** Case files whose category and type can be fixed from here. */
  fixable: ReadonlySet<string>;
  onFixType: (nodeId: string) => void;
  /** Switch topics on or off by `area`. */
  onToggle: (areas: string[], run: boolean) => void;
}> = ({ rows, fixable, onFixType, onToggle }) => {
  if (!rows.length) {
    return (
      <ListCard>
        <ListRow
          title="No Research Topics"
          description="This template doesn't research anything first."
        />
      </ListCard>
    );
  }
  return (
    <ListCard>
      <ResearchTopicRows
        rows={rows}
        topic={(r) => ({
          media: previewIcon([r.state]),
          control:
            r.state === "skipped" && r.doc_id && fixable.has(r.doc_id) ? (
              <>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => r.doc_id && onFixType(r.doc_id)}
                >
                  Fix Type
                </Button>
                {previewLabel([r.state])}
              </>
            ) : switchable(r) ? (
              <>
                {previewLabel([r.state])}
                <RunSwitch
                  title={r.title}
                  on={r.state === "will_run"}
                  onChange={(on) => onToggle([r.area], on)}
                />
              </>
            ) : (
              previewLabel([r.state])
            ),
          description:
            r.state === "blocked" || r.state === "skipped"
              ? r.reason ?? undefined
              : topicFiles(r)
        })}
        heading={(group) => {
          const states = group.map((r) => r.state);
          const skipped = states.filter((s) => s === "skipped").length;
          const reused = states.filter((s) => s === "ready").length;
          const omitted = states.filter((s) => s === "omitted").length;
          const toggles = group.filter(switchable);
          return {
            media: previewIcon(states),
            control: toggles.length ? (
              <>
                {previewLabel(states)}
                <RunSwitch
                  title={group[0].group ?? group[0].title}
                  on={toggles.some((r) => r.state === "will_run")}
                  onChange={(on) =>
                    onToggle(
                      toggles.map((r) => r.area),
                      on
                    )
                  }
                />
              </>
            ) : (
              previewLabel(states)
            ),
            description: [
              `${group.length} ${group.length === 1 ? "file" : "files"}`,
              reused ? `${reused} reused` : "",
              omitted ? `${omitted} omitted` : "",
              skipped ? `${skipped} skipped` : ""
            ]
              .filter(Boolean)
              .join(" · ")
          };
        }}
      />
    </ListCard>
  );
};

/**
 * Classic's Case Strategy: the old questionnaire the brief writers read,
 * filled from the case files where blank, with the criteria to argue. Nothing
 * is written until Generate Classic Draft.
 */
export const CaseStrategyStep: FC<Props> = ({
  draft,
  pick,
  research,
  onAnswersSaved,
  onBack,
  onStarted
}) => {
  const individualId = draft.individual_id;
  const { invalidate } = useCaseIde();
  const { fs } = useCaseData();
  const [data, setData] = useState<CaseStrategy | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [criteria, setCriteria] = useState<string[]>([]);
  const [edited, setEdited] = useState<Set<string>>(() => new Set());
  const [picker, setPicker] = useState<StrategyQuestion | null>(null);
  const [fillingAll, setFillingAll] = useState(false);
  const [classifying, setClassifying] = useState<string[] | null>(null);
  const [generating, setGenerating] = useState(false);
  const [omitted, setOmitted] = useState<ReadonlySet<string>>(
    () => new Set(draft.classic_omitted_research ?? [])
  );
  const preview = useMemo(
    () => withOmitted(research, omitted),
    [research, omitted]
  );
  const toggleResearch = (areas: string[], run: boolean) =>
    setOmitted((prev) => {
      const next = new Set(prev);
      for (const area of areas) {
        if (run) next.delete(area);
        else next.add(area);
      }
      return next;
    });
  // Answers typed but not saved yet: a refresh must not put the old value back.
  const pending = useRef(new Set<string>());
  const timers = useRef(new Map<string, number>());
  const autofillAsked = useRef(false);

  const load = useCallback(async () => {
    try {
      const next = await CaseStrategyAPI.get(individualId, draft.draft_type);
      setData(next);
      setLoadError(null);
      setAnswers((prev) => {
        const merged = { ...next.answers };
        for (const key of pending.current) merged[key] = prev[key];
        return merged;
      });
      if (next.criteria) setCriteria(next.criteria.selected);
      return next;
    } catch (error) {
      Sentry.captureException(error);
      setLoadError("Couldn't load the Draft Strategy.");
      return null;
    }
  }, [individualId, draft.draft_type]);

  // First open: fill blank answers from the case files once.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const first = await load();
      if (!alive || !first || autofillAsked.current) return;
      autofillAsked.current = true;
      const blank = first.questions.some(
        (q) => !hasContent(first.answers[q.variableName])
      );
      if (first.autofill.available && !first.autofill.status && blank) {
        try {
          await CaseStrategyAPI.autofill(individualId);
          if (alive) await load();
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [individualId, load]);

  const running =
    data?.autofill.status === "running" ||
    !!data?.questions.some((q) => q.fill_from_files.run?.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, load]);

  useEffect(
    () => () => {
      for (const timer of timers.current.values()) window.clearTimeout(timer);
    },
    []
  );

  const save = useCallback(
    (variable: string, path: string[], value: unknown) => {
      pending.current.add(variable);
      const existing = timers.current.get(variable);
      if (existing) window.clearTimeout(existing);
      timers.current.set(
        variable,
        window.setTimeout(async () => {
          timers.current.delete(variable);
          try {
            await QuestionnairesAPI.patchQuestionnaire(
              individualId,
              answerWrite(path, value)
            );
            onAnswersSaved();
          } catch (error) {
            Sentry.captureException(error);
            toast.error("Couldn't save that answer.");
          } finally {
            pending.current.delete(variable);
          }
        }, SAVE_DEBOUNCE_MS)
      );
    },
    [individualId, onAnswersSaved]
  );

  const setAnswer = (q: Question, path: string[], value: unknown) => {
    setAnswers((prev) => ({ ...prev, [q.variableName]: value }));
    setEdited((prev) => new Set(prev).add(q.variableName));
    save(q.variableName, path, value);
  };

  const toggleCriterion = async (name: string) => {
    if (!data?.criteria) return;
    const { options } = data.criteria;
    const next = criteria.includes(name)
      ? criteria.filter((c) => c !== name)
      : options.filter((o) => o === name || criteria.includes(o));
    setCriteria(next);
    try {
      await QuestionnairesAPI.patchQuestionnaire(individualId, {
        [data.criteria.field]: next
      });
      onAnswersSaved();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Couldn't save the criteria.");
    }
  };

  const fillField = async (q: StrategyQuestion, nodeIds: string[]) => {
    setPicker(null);
    try {
      await CaseStrategyAPI.fillField(individualId, q.variableName, nodeIds);
      await load();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Couldn't start reading those files."
      );
    }
  };

  const fillAll = async (nodeIds: string[]) => {
    setFillingAll(false);
    try {
      await CaseStrategyAPI.autofill(individualId, nodeIds);
      await load();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Couldn't start reading those files."
      );
    }
  };

  const resolve = async (q: StrategyQuestion, action: "accept" | "discard") => {
    try {
      await CaseStrategyAPI.resolveSuggestion(
        individualId,
        q.variableName,
        action
      );
      if (action === "accept") {
        pending.current.delete(q.variableName);
        setEdited((prev) => {
          const next = new Set(prev);
          next.delete(q.variableName);
          return next;
        });
        onAnswersSaved();
      }
      await load();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Couldn't update that answer.");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await legacyGenerateDraft(draft.draft_id, {
        templateId: pick.templateId,
        letterheadId: pick.letterheadId,
        omitResearch: preview
          .filter((r) => r.state === "omitted")
          .map((r) => r.area)
      });
      invalidate(["drafts"]);
      onStarted();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Couldn't start the classic draft."
      );
    } finally {
      setGenerating(false);
    }
  };

  const sections = useMemo(() => {
    const out = new Map<string, StrategyQuestion[]>();
    for (const q of data?.questions ?? []) {
      const key = q.section?.trim() || "Questions";
      out.set(key, [...(out.get(key) ?? []), q]);
    }
    return [...out.entries()];
  }, [data]);

  const autofillRunning = data?.autofill.status === "running";
  const unanswered = (data?.questions ?? []).filter(
    (q) => q.isRequired && !hasContent(answers[q.variableName])
  ).length;
  const needsCriteria = !!data?.criteria && criteria.length === 0;
  const meta = ["Classic", pick.templateName, pick.letterheadName]
    .filter(Boolean)
    .join(" · ");
  const files = fs.nodes;
  const titles = useMemo(
    () => new Map(files.map((n) => [n.id, nodeTitle(n)])),
    [files]
  );
  // Taken when the picker opens: a reload or tree poll must not reset its checks.
  const lastPicks = useMemo(() => {
    if (!fillingAll) return NO_IDS;
    const previous = data?.autofill.node_ids ?? [];
    return previous.length ? previous : pickableFiles(files).map((n) => n.id);
  }, [fillingAll]);
  const fixable = useMemo(
    () => new Set(pickableFiles(files).map((n) => n.id)),
    [files]
  );
  const uncategorizedIds = () =>
    pickableFiles(files)
      .filter((n) => !n.taxonomy_category && n.classified_by !== "attorney")
      .map((n) => n.id);
  const classified = () => {
    onAnswersSaved();
    void load();
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="case-strategy">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line-1 pr-3 pl-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Back"
              onClick={onBack}
            >
              <ArrowLeft />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Back</TooltipContent>
        </Tooltip>
        <span className="text-base font-medium text-fg">Draft Strategy</span>
        <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
          {meta}
        </span>
        {data && !autofillRunning && unanswered ? (
          <span className="text-sm text-fg-3">
            {unanswered} Unanswered · Left Out of the Draft
          </span>
        ) : null}
        {data?.autofill.available ? (
          <Button
            variant="secondary"
            disabled={autofillRunning || generating}
            onClick={() => setFillingAll(true)}
          >
            <FileSearch />
            Fill From Files
          </Button>
        ) : null}
        <Button
          variant="primary"
          disabled={!data || needsCriteria || autofillRunning || generating}
          onClick={() => void generate()}
          data-testid="classic-generate"
        >
          {generating ? <Spinner className="size-3.5" /> : null}
          Generate Classic Draft
        </Button>
      </div>
      {data ? (
        <AutofillStrip
          data={data}
          onRetry={() =>
            void CaseStrategyAPI.autofill(
              individualId,
              data.autofill.node_ids
            ).then(load)
          }
          onReview={() => setClassifying(uncategorizedIds())}
        />
      ) : null}
      {!data && !loadError ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <BrandLoader label="Loading Draft Strategy" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="mx-auto flex max-w-150 flex-col gap-7 px-6 pt-5 pb-10">
            {loadError || !data ? (
              <span className="text-sm text-danger">{loadError}</span>
            ) : (
              <>
                {data.criteria && data.criteria.options.length ? (
                  <section className="flex flex-col gap-3">
                    <SectionLabel
                      title={
                        data.criteria.field === "protected_ground"
                          ? "Protected Grounds"
                          : "Criteria to Argue"
                      }
                      meta={
                        criteria.length
                          ? `${criteria.length} of ${data.criteria.options.length}`
                          : "Pick at least one to generate"
                      }
                    />
                    <ListCard>
                      {data.criteria.options.map((name) => (
                        <label
                          key={name}
                          htmlFor={`strategy-criterion-${name}`}
                          className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-fill-1"
                        >
                          <Checkbox
                            id={`strategy-criterion-${name}`}
                            checked={criteria.includes(name)}
                            onCheckedChange={() => void toggleCriterion(name)}
                          />
                          <span className="min-w-0 flex-1 truncate text-base text-fg">
                            {name}
                          </span>
                        </label>
                      ))}
                    </ListCard>
                    <span className="text-sm text-fg-3">
                      The brief writes a section for each one you keep.
                    </span>
                  </section>
                ) : null}

                {sections.map(([section, questions]) => (
                  <section key={section} className="flex flex-col gap-4">
                    <SectionLabel
                      title={section}
                      meta={`${
                        questions.filter((q) =>
                          hasContent(answers[q.variableName])
                        ).length
                      } of ${questions.length} answered`}
                    />
                    {questions.map((q) => (
                      <StrategyField
                        key={q.id}
                        question={q}
                        path={q.path}
                        answers={answers}
                        filling={
                          autofillRunning &&
                          !hasContent(answers[q.variableName])
                        }
                        fromCase={
                          data.autofill.filled.includes(q.variableName) &&
                          !edited.has(q.variableName)
                        }
                        edited={edited.has(q.variableName)}
                        nodesById={titles}
                        onChange={setAnswer}
                        onPick={() => setPicker(q)}
                        onRetry={(ids) => void fillField(q, ids)}
                        onResolve={(action) => void resolve(q, action)}
                      />
                    ))}
                  </section>
                ))}

                <section className="flex flex-col gap-3">
                  <SectionLabel
                    title="Research"
                    meta={`${
                      preview.filter(
                        (r) => r.state === "ready" || r.state === "will_run"
                      ).length
                    } topics run when you generate`}
                  />
                  <ResearchPreview
                    rows={preview}
                    fixable={fixable}
                    onFixType={(id) => setClassifying([id])}
                    onToggle={toggleResearch}
                  />
                </section>
                <span className="text-sm text-fg-4">
                  Answers save as you type and are shared with this case's other
                  classic drafts.
                </span>
              </>
            )}
          </div>
        </div>
      )}
      {fillingAll && data ? (
        <FillFromFilesDialog
          open
          onOpenChange={(open) => !open && setFillingAll(false)}
          title="Fill Draft Strategy From Files"
          description="Blank answers are filled. Answered questions get a suggestion you can use or discard."
          actionLabel="Fill Answers"
          limit={null}
          nodes={files}
          suggested={NO_IDS}
          previous={lastPicks}
          onFill={(ids) => void fillAll(ids)}
          onEditCategory={(node) => setClassifying([node.id])}
        />
      ) : null}
      {picker ? (
        <FillFromFilesDialog
          open
          onOpenChange={(open) => !open && setPicker(null)}
          title={`Fill ${picker.questionText} From Files`}
          description={
            hasContent(answers[picker.variableName])
              ? "This question has an answer. The result comes back as a suggestion you can use or discard."
              : "Only the files you check are read. Suggested files are the ones this question usually draws on."
          }
          actionLabel="Fill Field"
          nodes={files}
          suggested={picker.fill_from_files.suggested_node_ids}
          previous={picker.fill_from_files.sources}
          onFill={(ids) => void fillField(picker, ids)}
          onEditCategory={(node) => setClassifying([node.id])}
        />
      ) : null}
      {classifying ? (
        <ClassifyFilesDialog
          individualId={individualId}
          open
          onOpenChange={(open) => !open && setClassifying(null)}
          nodes={pickableFiles(files).filter((n) => classifying.includes(n.id))}
          onSave={fs.setClassification}
          onSaved={classified}
        />
      ) : null}
    </div>
  );
};
